import Phaser from 'phaser';
import { ScoreService } from '../services/ScoreService';

/**
 * HighScoreScene: Displays the local high score leaderboard (top 10).
 * All text uses neon glow effects for consistent visual style and contrast.
 */
export class HighScoreScene extends Phaser.Scene {
  constructor() {
    super({ key: 'HighScoreScene' });
  }

  create(): void {
    const { width, height } = this.cameras.main;
    const scoreService = ScoreService.getInstance();
    const highScores = scoreService.getHighScores();

    // Starfield background
    for (let i = 0; i < 60; i++) {
      const star = this.add.image(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height),
        'star'
      );
      star.setAlpha(Phaser.Math.FloatBetween(0.2, 0.7));
      star.setDepth(-1);
    }

    // Title with neon glow
    this.add.text(width / 2, 40, 'HIGH SCORES', {
      fontSize: '28px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00ffff',
        blur: 15,
        fill: true,
        stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Column headers with green glow
    const headerStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '12px',
      color: '#00ff41',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00ff41',
        blur: 6,
        fill: true,
        stroke: true,
      },
    };

    this.add.text(width * 0.2, 90, 'RANK', headerStyle).setOrigin(0, 0.5);
    this.add.text(width * 0.4, 90, 'NAME', headerStyle).setOrigin(0, 0.5);
    this.add.text(width * 0.7, 90, 'SCORE', headerStyle).setOrigin(0, 0.5);

    // Score entries
    if (highScores.length === 0) {
      this.add.text(width / 2, height / 2, 'NO SCORES YET', {
        fontSize: '14px',
        color: '#8888aa',
        fontFamily: '"Press Start 2P", monospace',
        shadow: {
          offsetX: 0,
          offsetY: 0,
          color: '#6666aa',
          blur: 8,
          fill: true,
          stroke: true,
        },
      }).setOrigin(0.5, 0.5);
    } else {
      highScores.forEach((entry, index) => {
        const y = 130 + index * 40;
        const color = index === 0 ? '#ffff00' : '#ffffff';
        const glowColor = index === 0 ? '#ffff00' : '#aaaaff';

        this.add.text(width * 0.2, y, `${index + 1}.`, {
          fontSize: '14px',
          color,
          fontFamily: '"Press Start 2P", monospace',
          shadow: {
            offsetX: 0, offsetY: 0, color: glowColor, blur: 4, fill: true, stroke: true,
          },
        });

        this.add.text(width * 0.4, y, entry.initials, {
          fontSize: '14px',
          color,
          fontFamily: '"Press Start 2P", monospace',
          shadow: {
            offsetX: 0, offsetY: 0, color: glowColor, blur: 4, fill: true, stroke: true,
          },
        });

        this.add.text(width * 0.7, y, `${entry.score}`, {
          fontSize: '14px',
          color,
          fontFamily: '"Press Start 2P", monospace',
          shadow: {
            offsetX: 0, offsetY: 0, color: glowColor, blur: 4, fill: true, stroke: true,
          },
        });
      });
    }

    // Back button with glow - highlighted by default since it's the only interactive element
    const backBtn = this.add.text(width / 2, height - 50, 'BACK', {
      fontSize: '16px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00ffff',
        blur: 12,
        fill: true,
        stroke: true,
      },
    });
    backBtn.setOrigin(0.5, 0.5);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => {
      backBtn.setColor('#00ffff');
      backBtn.setShadow(0, 0, '#00ffff', 12, true, true);
    });
    backBtn.on('pointerout', () => {
      backBtn.setColor('#00ffff');
      backBtn.setShadow(0, 0, '#00ffff', 12, true, true);
    });
    backBtn.on('pointerdown', () => this.scene.start('TitleScene'));

    // Focus arrow indicator (▶) next to BACK button
    const focusArrow = this.add.text(0, 0, '▶', {
      fontSize: '14px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ffff', blur: 10, fill: true, stroke: true,
      },
    });
    focusArrow.setOrigin(0.5, 0.5);
    const backBounds = backBtn.getBounds();
    focusArrow.setPosition(backBounds.x - 20, backBounds.y + backBounds.height / 2);

    // Pulsing animation for focus arrow
    this.tweens.add({
      targets: focusArrow,
      alpha: { from: 0.6, to: 1.0 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Help text
    this.add.text(width / 2, height - 20, 'ESC / ENTER / SPACE  BACK', {
      fontSize: '7px',
      color: '#8888aa',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#8888aa', blur: 4, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Keyboard shortcuts - ESC, Enter, Space, Backspace all go back
    this.input.keyboard?.on('keydown-ESC', () => this.scene.start('TitleScene'));
    this.input.keyboard?.on('keydown-ENTER', () => this.scene.start('TitleScene'));
    this.input.keyboard?.on('keydown-SPACE', () => this.scene.start('TitleScene'));
    this.input.keyboard?.on('keydown-BACKSPACE', () => this.scene.start('TitleScene'));
  }

  /**
   * Clean up keyboard and input listeners when scene is shut down.
   * Prevents memory leaks from accumulated listeners across scene transitions.
   */
  shutdown(): void {
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();
    // Kill all tweens (focus arrow pulse)
    this.tweens.killAll();
  }
}
